// src/pages/customers/tabs/InfoTab.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import { FiMapPin, FiEdit2, FiSave, FiUserPlus, FiLink2, FiX } from "react-icons/fi";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import API_BASE_URL from "../../../../utils/config";
import { getCustomerById, updateCustomer } from "../../customerService";
import { useParams, useNavigate } from "react-router-dom";

const containerStyle = { width: "100%", height: "300px" };
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India fallback

// ---------- small UI helpers ----------
const moneyINR = (n) => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

const badgeCls = (tone) => {
  switch (tone) {
    case "green":
      return "bg-green-100 text-green-800 border-green-200";
    case "indigo":
      return "bg-indigo-100 text-indigo-800 border-indigo-200";
    case "amber":
      return "bg-amber-100 text-amber-800 border-amber-200";
    case "gray":
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const Badge = ({ tone = "gray", children }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${badgeCls(
      tone
    )}`}
  >
    {children}
  </span>
);

const CommissionTypeBadge = ({ type }) => {
  if (type === "all_products") return <Badge tone="green">All products</Badge>;
  if (type === "product_specific") return <Badge tone="indigo">Selected products</Badge>;
  if (type === "excluded_products") return <Badge tone="amber">Exclude products</Badge>;
  return <Badge tone="gray">—</Badge>;
};
// --------------------------------------

export default function InfoTab() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [mapPosition, setMapPosition] = useState(null);

  // doctor link UI
  const [showDoctorPanel, setShowDoctorPanel] = useState(false);
  const [doctorMode, setDoctorMode] = useState("existing");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [newDoc, setNewDoc] = useState({ name: "", phone: "", specialization: "" });

  // auto-fetched mapped doctors
  const [linkedDoctors, setLinkedDoctors] = useState([]);
  const [linkedLoading, setLinkedLoading] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const fetchLinkedDoctors = async () => {
    if (!id) return;
    setLinkedLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/customers/by-customer/${id}`, authHeader);
      const arr = Array.isArray(res.data) ? res.data : [];
      setLinkedDoctors(arr);
    } catch (e) {
      if (e?.response?.status === 404) setLinkedDoctors([]);
      else console.warn("fetchLinkedDoctors failed", e?.response?.data || e);
    } finally {
      setLinkedLoading(false);
    }
  };

  const goToAddDoctor = () => {
    navigate(`/doctors/add?linkToCustomer=${id}&redirect=/customers/${id}`);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getCustomerById(id);
        if (!mounted) return;

        if (!data) {
          setCustomer(null);
          setFormData({});
          setMapPosition(null);
          return;
        }

        setCustomer(data);
        setFormData(data);

        const latNum = Number(data?.lat);
        const lngNum = Number(data?.lng);
        if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
          setMapPosition({ lat: latNum, lng: lngNum });
        } else {
          setMapPosition(null);
        }
      } catch (err) {
        console.error("Failed to load customer:", err);
        if (!mounted) return;
        setCustomer(null);
        setFormData({});
        setMapPosition(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    fetchLinkedDoctors();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="p-6 text-gray-600">Loading customer info...</div>;
  if (!customer) return <div className="p-6 text-red-500">Customer not found</div>;

  const handleInputChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    try {
      const updated = await updateCustomer(id, formData);
      setCustomer(updated);
      setEditMode(false);
      alert("Customer info updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update customer.");
    }
  };

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMapPosition({ lat, lng });
    setFormData({ ...formData, lat, lng });
  };

  const openGoogleMaps = () => {
    if (customer?.lat && customer?.lng) {
      const url = `https://www.google.com/maps?q=${customer.lat},${customer.lng}`;
      window.open(url, "_blank");
    }
  };

  // ---- Doctor helpers ----
  const loadDoctorOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 1) return [];
    const res = await axios.get(
      `${API_BASE_URL}/api/doctors/search?q=${encodeURIComponent(inputValue.trim())}`,
      authHeader
    );
    return (res.data || []).map((d) => ({ value: d.id, label: d.name }));
  };

  const tryFindLinkedCustomer = async (doctorId) => {
    try {
      const byDoctor = await axios.get(
        `${API_BASE_URL}/api/customers/by-doctor/${doctorId}`,
        authHeader
      );
      if (byDoctor?.data?.id) return byDoctor.data;
    } catch (_) {}
    try {
      const list = await axios.get(
        `${API_BASE_URL}/api/customers?doctor_id=${doctorId}`,
        authHeader
      );
      const arr = Array.isArray(list?.data) ? list.data : [];
      return arr[0] || null;
    } catch (_) {}
    return null;
  };

  const linkExistingDoctor = async (opt) => {
    if (!opt?.value) return;
    setLinkError("");
    setLinking(true);
    try {
      const prev = await tryFindLinkedCustomer(opt.value);
      if (prev && String(prev.id) !== String(id)) {
        const confirmMove = window.confirm(
          `This doctor is already linked to "${prev.name}". Do you want to move the link to this customer?`
        );
      }

      await axios.put(`${API_BASE_URL}/api/doctors/link`,
      {doctorId: opt.value,customerId: id,customerType: customer.type || null},authHeader);

      const fresh = await getCustomerById(id);
      setCustomer(fresh);
      setFormData(fresh);
      await fetchLinkedDoctors();
      setShowDoctorPanel(false);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.status === 409
          ? "Doctor is already linked to another customer."
          : "Failed to link doctor.");
      setLinkError(msg);
    } finally {
      setLinking(false);
    }
  };

  const unlinkDoctor = async () => {
    if (!customer?.doctor_id) return;
    if (!window.confirm("Unlink the current doctor from this customer?")) return;
    setLinking(true);
    setLinkError("");
    try {
      await axios.put(`${API_BASE_URL}/api/customers/${id}`, { doctor_id: null }, authHeader);
      const fresh = await getCustomerById(id);
      setCustomer(fresh);
      setFormData(fresh);
      await fetchLinkedDoctors();
      setShowDoctorPanel(false);
    } catch (e) {
      setLinkError(e?.response?.data?.message || "Failed to unlink doctor.");
    } finally {
      setLinking(false);
    }
  };

  const createAndLinkDoctor = async () => {
    if (!newDoc.name?.trim()) {
      alert("Doctor name is required.");
      return;
    }
    setLinking(true);
    setLinkError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/doctors`,
        {
          name: newDoc.name.trim(),
          phone: newDoc.phone?.trim() || null,
          specialization: newDoc.specialization?.trim() || null,
        },
        authHeader
      );

      const doctorId =
        res?.data?.id ??
        res?.data?.doctor_id ??
        res?.data?.doctorId ??
        res?.data?._id ??
        res?.data?.doctor?.id ??
        null;

      if (!doctorId) throw new Error("Doctor created but id missing.");

      await axios.put(`${API_BASE_URL}/api/customers/${id}`, { doctor_id: doctorId }, authHeader);

      const fresh = await getCustomerById(id);
      setCustomer(fresh);
      setFormData(fresh);
      await fetchLinkedDoctors();
      setShowDoctorPanel(false);
      setNewDoc({ name: "", phone: "", specialization: "" });
    } catch (e) {
      setLinkError(e?.response?.data?.message || "Failed to create/link doctor.");
    } finally {
      setLinking(false);
    }
  };

  const Card = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-lg p-4 hover:shadow-lg transition duration-300 min-h-[120px]">
      <h3 className="text-lg font-semibold text-blue-800 mb-2">{title}</h3>
      {children}
    </div>
  );

  // pull the mapping (type/commission) for *this* customer from the row.customers JSON
  const mappingForThisCustomer = (row) => {
    try {
      const list = typeof row.customers === "string" ? JSON.parse(row.customers) : row.customers;
      if (!Array.isArray(list)) return null;
      return list.find((x) => String(x.customer_id) === String(id)) || null;
    } catch {
      return null;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-blue-900">Customer Information</h2>
        <button
          onClick={() => (editMode ? handleSave() : setEditMode(true))}
          className="flex items-center bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 shadow text-sm"
        >
          {editMode ? <FiSave className="mr-2" /> : <FiEdit2 className="mr-2" />}
          {editMode ? "Save" : "Edit"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name & Type */}
        <Card title="Name & Type">
          {editMode ? (
            <>
              <input
                name="name"
                value={formData.name || ""}
                onChange={handleInputChange}
                className="mt-1 w-full border rounded p-2 text-sm"
              />
              <input
                name="type"
                value={formData.type || ""}
                onChange={handleInputChange}
                className="mt-2 w-full border rounded p-2 text-sm"
              />
            </>
          ) : (
            <>
              <p className="text-gray-700">{customer.name}</p>
              <p className="text-gray-500 text-sm">{customer.type || "N/A"}</p>
            </>
          )}
        </Card>

        {/* Contact */}
        <Card title="Contact">
          {editMode ? (
            <>
              <input
                name="phone"
                value={formData.phone || ""}
                onChange={handleInputChange}
                className="mt-1 w-full border rounded p-2 text-sm"
              />
              <input
                name="email"
                value={formData.email || ""}
                onChange={handleInputChange}
                className="mt-2 w-full border rounded p-2 text-sm"
              />
            </>
          ) : (
            <>
              <p className="text-gray-700">Phone: {customer.phone || "N/A"}</p>
              <p className="text-gray-700">Email: {customer.email || "N/A"}</p>
            </>
          )}
        </Card>

        {/* Address */}
        <Card title="Address">
          {editMode ? (
            <textarea
              name="address"
              value={formData.address || ""}
              onChange={handleInputChange}
              className="mt-1 w-full border rounded p-2 text-sm"
            />
          ) : (
            <p className="text-gray-700">{customer.address || "N/A"}</p>
          )}
        </Card>

        {/* GST & DL */}
        <Card title="GST & DL">
          {editMode ? (
            <>
              <input
                name="gst_number"
                value={formData.gst_number || ""}
                onChange={handleInputChange}
                placeholder="GST Number"
                className="mt-1 w-full border rounded p-2 text-sm"
              />
              <input
                name="dl_no"
                value={formData.dl_no || ""}
                onChange={handleInputChange}
                placeholder="DL Number"
                className="mt-2 w-full border rounded p-2 text-sm"
              />
            </>
          ) : (
            <>
              <p className="text-gray-700">GST Number: {customer.gst_number || "N/A"}</p>
              <p className="text-gray-700">DL Number: {customer.dl_no || "N/A"}</p>
            </>
          )}
        </Card>
{/* Location */}
        <div className="md:col-span-2">
          <Card title="Location">
            <div className="flex items-center gap-2 mb-2 text-blue-800 font-semibold">
              <FiMapPin className="text-red-500 text-lg" />
              <span>Customer Location</span>
            </div>
            {editMode ? (
              <>
                <div className="flex gap-2 mt-1">
                  <input
                    name="lat"
                    value={formData.lat || ""}
                    onChange={handleInputChange}
                    placeholder="Latitude"
                    className="border p-2 rounded w-1/2 text-sm"
                  />
                  <input
                    name="lng"
                    value={formData.lng || ""}
                    onChange={handleInputChange}
                    placeholder="Longitude"
                    className="border p-2 rounded w-1/2 text-sm"
                  />
                </div>
                {isLoaded && (
                  <div className="mt-3 rounded overflow-hidden">
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={mapPosition || DEFAULT_CENTER}
                      zoom={12}
                      onClick={handleMapClick}
                    >
                      {mapPosition && <Marker position={mapPosition} />}
                    </GoogleMap>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-gray-700">Latitude: {customer.lat || "N/A"}</p>
                <p className="text-gray-700 mb-2">Longitude: {customer.lng || "N/A"}</p>
                {customer.lat && customer.lng && (
                  <Tippy content={customer.address || "View on Google Maps"}>
                    <button
                      onClick={openGoogleMaps}
                      className="text-blue-600 hover:text-blue-800 text-2xl p-2 rounded shadow-md"
                    >
                      <FiMapPin />
                    </button>
                  </Tippy>
                )}
              </>
            )}
          </Card>
        </div>
        {/* Doctor */}
        <div className="md:col-span-2">

        <Card title="Doctor">
          {linkError && (
            <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 text-sm">
              {linkError}
            </div>
          )}

          {/* Pretty list like your screenshot */}
          <div className="mb-3" >
            <div className="text-sm font-medium text-gray-700 mb-2">Linked doctors for this customer</div>
            {linkedLoading ? (
              <div className="text-xs text-gray-500">Loading doctors…</div>
            ) : linkedDoctors.length ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Doctor Name</th>
                      <th className="px-4 py-2 text-left font-semibold">Linked Customer</th>
                      <th className="px-4 py-2 text-right font-semibold">Total Commission</th>
                      <th className="px-4 py-2 text-right font-semibold">Commission Paid</th>
                      <th className="px-4 py-2 text-right font-semibold">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {linkedDoctors.map((d) => {
                      const map = mappingForThisCustomer(d); // type/commission for THIS customer
                      return (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <button
                              className="text-blue-700 hover:underline font-semibold"
                              onClick={() => navigate(`/doctors/${d.id}`)}
                            >
                              {d.name}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-blue-800 font-medium">{customer.name}</span>
                              <Badge tone="gray">{map?.type || "-"}</Badge>
                              <Badge tone="gray">{map?.commission_value ? `${map.commission_value}%` : "-"}</Badge>
                              <CommissionTypeBadge type={map?.commission_type} />
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right">{moneyINR(d.total_commission)}</td>
                          <td className="px-4 py-2 text-right">{moneyINR(d.commission_paid)}</td>
                          <td className="px-4 py-2 text-right">{moneyINR(d.balance_commission)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-xs text-gray-500">No linked doctors yet.</div>
            )}
          </div>


          {/* Primary link controls (customers.doctor_id) */}
          {!customer.doctor_id ? (
            <>
              {!showDoctorPanel ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    className="inline-flex items-center bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
                    onClick={() => {
                      setDoctorMode("existing");
                      setShowDoctorPanel(true);
                    }}
                  >
                    <FiLink2 className="mr-2" /> Link Existing
                  </button>
                  <button
                    className="inline-flex items-center bg-emerald-600 text-white px-3 py-2 rounded text-sm hover:bg-emerald-700"
                    onClick={goToAddDoctor}
                  >
                    <FiUserPlus className="mr-2" /> Add & Link New
                  </button>
                </div>
              ) : (
                <div className="border rounded-md p-3 mt-1">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">Link Existing Doctor</div>
                    <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowDoctorPanel(false)}>
                      <FiX />
                    </button>
                  </div>

                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadDoctorOptions}
                    onChange={(opt) => linkExistingDoctor(opt)}
                    isClearable
                    placeholder="Search doctor by name…"
                    classNamePrefix="react-select"
                    isDisabled={linking}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Pick a doctor to link. If the doctor is already linked elsewhere, you’ll be asked to move the link.
                  </p>
                </div>
              )}
              {!showDoctorPanel && <p className="text-gray-500 text-sm mt-2">No primary doctor linked yet.</p>}
            </>
          ) : (
            <>
              <div className="text-gray-700 flex items-center gap-2 flex-wrap">
                <span>
                  Primary doctor: <span className="font-medium">{customer.doctor_name || "—"}</span>
                </span>
                <button
                  className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-100 border border-blue-200"
                  onClick={() => {
                    setDoctorMode("existing");
                    setShowDoctorPanel(true);
                  }}
                >
                  Change
                </button>
                <button
                  className="inline-flex items-center bg-rose-50 text-rose-700 px-2 py-1 rounded text-xs hover:bg-rose-100 border border-rose-200"
                  onClick={unlinkDoctor}
                  disabled={linking}
                >
                  Unlink
                </button>
              </div>

              {showDoctorPanel && doctorMode === "existing" && (
                <div className="border rounded-md p-3 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">Change Linked Doctor</div>
                    <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowDoctorPanel(false)}>
                      <FiX />
                    </button>
                  </div>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadDoctorOptions}
                    onChange={(opt) => linkExistingDoctor(opt)}
                    isClearable
                    placeholder="Search new doctor…"
                    classNamePrefix="react-select"
                    isDisabled={linking}
                  />
                  <div className="text-xs text-gray-500 mt-2">
                    Selecting a different doctor will replace the current link. If that doctor is linked elsewhere, you can move the link here.
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
</div>

        
      </div>
    </div>
  );
}
